import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import api from '../services/api';

const statusLabels = {
  pending: 'Ожидает',
  active: 'В работе',
  completed: 'Завершена',
  rejected: 'Отклонена'
};

const themeLabels = {
  moto: 'Мототехника',
  delivery: 'Доставка',
  payment: 'Оплата',
  undefined: 'Без темы'
};

function ApplicationCard({ application, managers, onUpdate }) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleStatusChange = async (newStatus) => {
    try {
      setIsUpdating(true);
      await api.updateApplication(application.id, { status: newStatus });
      toast.success('Статус заявки обновлен');
      onUpdate();
    } catch (error) {
      toast.error('Ошибка при обновлении статуса');
      console.error('Error updating application status:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAssignManager = async (managerId) => {
    try {
      setIsUpdating(true);
      await api.assignManager(application.id, managerId);
      toast.success('Менеджер назначен');
      setShowAssignModal(false);
      onUpdate();
    } catch (error) {
      toast.error('Ошибка при назначении менеджера');
      console.error('Error assigning manager:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const availableManagers = managers.filter(m => m.status === 'active');

  return (
    <>
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <h3 className="text-lg font-medium text-gray-900">
                Заявка #{application.id}
              </h3>
              <span className={`status-badge status-${application.status}`}>
                {statusLabels[application.status] || application.status}
              </span>
            </div>

            <div className="text-sm text-gray-600 mb-3">
              <p><strong>Тема:</strong> {themeLabels[application.theme] || application.theme}</p>
              <p><strong>Создана:</strong> {formatDate(application.created_at)}</p>
              {application.user_data && (
                <p><strong>Пользователь:</strong> {application.user_data.name || 'Не указан'}</p>
              )}
              {application.assigned_manager && (
                <p><strong>Менеджер:</strong> {application.assigned_manager}</p>
              )}
            </div>

            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                {showDetails ? 'Скрыть детали' : 'Показать детали'}
              </button>
            </div>

            {showDetails && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Собранные данные:</h4>
                {application.collected_data && Object.keys(application.collected_data).length > 0 ? (
                  <div className="space-y-1">
                    {Object.entries(application.collected_data).map(([key, value]) => (
                      <div key={key} className="text-sm text-gray-600">
                        <strong>{key}:</strong> {String(value)}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Данные не собраны</p>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col space-y-2 ml-4">
            {application.status === 'pending' && (
              <>
                <button
                  onClick={() => setShowAssignModal(true)}
                  disabled={isUpdating}
                  className="px-3 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded hover:bg-blue-200 disabled:opacity-50"
                >
                  Назначить
                </button>
                <button
                  onClick={() => handleStatusChange('active')}
                  disabled={isUpdating}
                  className="px-3 py-1 text-xs font-medium text-green-700 bg-green-100 rounded hover:bg-green-200 disabled:opacity-50"
                >
                  В работу
                </button>
              </>
            )}
            
            {application.status === 'active' && (
              <button
                onClick={() => handleStatusChange('completed')}
                disabled={isUpdating}
                className="px-3 py-1 text-xs font-medium text-green-700 bg-green-100 rounded hover:bg-green-200 disabled:opacity-50"
              >
                Завершить
              </button>
            )}
          </div>
        </div>
      </div>

      {showAssignModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Назначить менеджера
            </h3>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {availableManagers.length === 0 ? (
                <p className="text-sm text-gray-500">Нет доступных менеджеров</p>
              ) : (
                availableManagers.map((manager) => (
                  <button
                    key={manager.id}
                    onClick={() => handleAssignManager(manager.id)}
                    disabled={isUpdating}
                    className="w-full text-left p-3 border rounded hover:bg-gray-50 disabled:opacity-50"
                  >
                    <div className="font-medium">{manager.name}</div>
                    <div className="text-sm text-gray-600">
                      Загрузка: {manager.current_load || 0} заявок
                    </div>
                  </button>
                ))
              )}
            </div>
            <div className="mt-4 flex justify-end space-x-2">
              <button
                onClick={() => setShowAssignModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ApplicationCard;